import { FC, useMemo, useState } from 'react';
import axios from 'axios';
import { motion ,Variants} from 'framer-motion';
import { useDispatch } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ApiConstants } from '../../../main/constants/ApiConstants';
import { setUser } from '../../../main/store/stores/user/user.store';
import AuthSplitLayout from '../shared/AuthSplitLayout';
import './Register.css';

const publicApi = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
});

const fieldVariants: Variants = {
  hidden: {
    opacity: 0,
    y: 16,
  },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: 0.1 + i * 0.05,
      type: "spring",
      stiffness: 100,
      damping: 20,
    },
  }),
};

type RegisterPageProps = {
  embedded?: boolean;
};

const RegisterPage: FC<RegisterPageProps> = ({ embedded = false }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const canSubmit = useMemo(
    () =>
      firstName.trim().length > 0 &&
      lastName.trim().length > 0 &&
      email.trim().length > 0 &&
      password.trim().length >= 6 &&
      confirmPassword.trim().length >= 6,
    [confirmPassword, email, firstName, lastName, password]
  );

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!canSubmit) {
      setError(t('login.registerRequiredFields'));
      return;
    }

    if (password !== confirmPassword) {
      setError(t('login.registerPasswordsMismatch'));
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      const normalizedEmail = email.trim().toLowerCase();
      const response = await publicApi.post(ApiConstants.publicUser.register, {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: normalizedEmail,
        phone: phone.trim(),
        password: password.trim(),
        confirmPassword: confirmPassword.trim(),
      });

      dispatch(setUser({ ...(response.data as any), pageRedirect: 1 }));
      navigate(`/activate-account?email=${encodeURIComponent(normalizedEmail)}`);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.response?.data || t('login.registerUnable'));
    } finally {
      setSubmitting(false);
    }
  };

  const content = (
    <>
      {/* Badge */}
      <motion.div
        className="registerBadge"
        variants={{
          hidden: { opacity: 0, y: -10 },
          visible: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 100, damping: 18 } },
        }}
        initial="hidden"
        animate="visible"
      >
        <div className="registerAvatarWrap">
          <svg className="registerAvatarIcon" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
            <circle cx="8.5" cy="7" r="4" />
            <line x1="20" y1="8" x2="20" y2="14" />
            <line x1="23" y1="11" x2="17" y2="11" />
          </svg>
        </div>
        <div>
          <p className="registerEyebrow">{t('login.registerEyebrow')}</p>
          <h1 className="registerTitle">{t('login.createAccount')}</h1>
          <p className="registerSubtitle">{t('login.registerSubtitle')}</p>
        </div>
      </motion.div>

      {/* Form */}
      <form className="registerForm" noValidate onSubmit={handleSubmit}>
        <div className="registerGrid">
          <motion.div custom={0} variants={fieldVariants} initial="hidden" animate="visible">
            <label className="authFieldLabel">{t('generic.firstName')}</label>
            <input
              className="authInput"
              required
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              autoComplete="given-name"
              placeholder={t('generic.firstName')}
            />
          </motion.div>

          <motion.div custom={1} variants={fieldVariants} initial="hidden" animate="visible">
            <label className="authFieldLabel">{t('generic.lastName')}</label>
            <input
              className="authInput"
              required
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              autoComplete="family-name"
              placeholder={t('generic.lastName')}
            />
          </motion.div>

          <motion.div className="registerFull" custom={2} variants={fieldVariants} initial="hidden" animate="visible">
            <label className="authFieldLabel">{t('generic.email')}</label>
            <input
              className="authInput"
              required
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              placeholder="you@example.com"
            />
          </motion.div>

          <motion.div className="registerFull" custom={3} variants={fieldVariants} initial="hidden" animate="visible">
            <label className="authFieldLabel">{t('generic.phoneNumber')}</label>
            <input
              className="authInput"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              autoComplete="tel"
              placeholder="+355 6X XXX XXXX"
            />
          </motion.div>

          <motion.div custom={4} variants={fieldVariants} initial="hidden" animate="visible">
            <label className="authFieldLabel">{t('generic.password')}</label>
            <input
              className="authInput"
              required
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              placeholder="••••••••"
            />
          </motion.div>

          <motion.div custom={5} variants={fieldVariants} initial="hidden" animate="visible">
            <label className="authFieldLabel">{t('generic.confirmPassword')}</label>
            <input
              className="authInput"
              required
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
              placeholder="••••••••"
            />
          </motion.div>
        </div>

        {/* Error */}
        {error && (
          <motion.div
            className="registerError"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            transition={{ duration: 0.2 }}
          >
            {error}
          </motion.div>
        )}

        {/* Submit */}
        <motion.div custom={6} variants={fieldVariants} initial="hidden" animate="visible">
          <button
            type="submit"
            className="authBtn authBtn--primary"
            disabled={submitting || !canSubmit}
          >
            {submitting ? (
              <span className="authBtnLoader" />
            ) : (
              <>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="8.5" cy="7" r="4" />
                  <line x1="20" y1="8" x2="20" y2="14" />
                  <line x1="23" y1="11" x2="17" y2="11" />
                </svg>
                {t('login.createAccount')}
              </>
            )}
          </button>
        </motion.div>
      </form>
    </>
  );

  if (embedded) {
    return content;
  }

  return (
    <AuthSplitLayout
      mode="register"
      footer={
        <>
          {t('login.registerAlreadyHaveAccount')} <Link to="/login">{t('generic.login')}</Link>
        </>
      }
    >
      {content}
    </AuthSplitLayout>
  );
};

export default RegisterPage;
